import { Alert, Card, Flex, Progress, Radio, Space, Typography } from 'antd'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/router'

import { WorkspacePageShell, useWorkspaceRoute } from './WorkspacePageShell'

const { Text, Title } = Typography

type BillingPeriod = 'current' | 'previous' | 'quarter'

type UsageMetric = {
  key: string
  label: string
  used: number
  limit: number
  unit: string
}

const USAGE_DATA: Record<BillingPeriod, UsageMetric[]> = {
  current: [
    {
      key: 'api',
      label: 'API requests',
      used: 45_000,
      limit: 100_000,
      unit: ''
    },
    { key: 'storage', label: 'Storage', used: 2.3, limit: 5, unit: 'GB' },
    {
      key: 'ai',
      label: 'AI tokens',
      used: 780_000,
      limit: 1_000_000,
      unit: ''
    },
    { key: 'users', label: 'Active users', used: 12, limit: 25, unit: '' }
  ],
  previous: [
    {
      key: 'api',
      label: 'API requests',
      used: 38_000,
      limit: 100_000,
      unit: ''
    },
    { key: 'storage', label: 'Storage', used: 2.1, limit: 5, unit: 'GB' },
    {
      key: 'ai',
      label: 'AI tokens',
      used: 620_000,
      limit: 1_000_000,
      unit: ''
    },
    { key: 'users', label: 'Active users', used: 10, limit: 25, unit: '' }
  ],
  quarter: [
    {
      key: 'api',
      label: 'API requests',
      used: 120_000,
      limit: 300_000,
      unit: ''
    },
    { key: 'storage', label: 'Storage', used: 2.3, limit: 5, unit: 'GB' },
    {
      key: 'ai',
      label: 'AI tokens',
      used: 2_100_000,
      limit: 3_000_000,
      unit: ''
    },
    { key: 'users', label: 'Active users', used: 15, limit: 25, unit: '' }
  ]
}

const formatNumber = (n: number): string => {
  if (n >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(1)}M`
  }
  if (n >= 1_000) {
    return `${(n / 1_000).toFixed(0)}K`
  }
  return String(n)
}

export const WorkspaceBillingPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { workspaceId } = useWorkspaceRoute()
  void workspaceId
  const [period, setPeriod] = useState<BillingPeriod>('current')

  const metrics = USAGE_DATA[period]

  const overageMetrics = useMemo(
    () =>
      metrics
        .map((m) => ({ ...m, pct: (m.used / m.limit) * 100 }))
        .filter((m) => m.pct > 80),
    [metrics]
  )

  const periodLabel =
    period === 'current'
      ? t('workspace.billingPage.period.current')
      : period === 'previous'
        ? t('workspace.billingPage.period.previous')
        : t('workspace.billingPage.period.quarter')

  return (
    <WorkspacePageShell
      sectionTitle={t('workspace.billingPage.sectionTitle')}
      sectionDescription={t('workspace.billingPage.sectionDescription')}
    >
      <Flex vertical gap={16}>
        <div>
          <button
            type="button"
            style={{
              background: 'none',
              border: 'none',
              color: '#1677ff',
              cursor: 'pointer',
              padding: 0
            }}
            onClick={() => navigate(paths.billing)}
          >
            {t('workspace.billingPage.goToAccountBilling')}
          </button>
        </div>

        <Card variant="outlined">
          <Flex vertical gap={16}>
            <Flex justify="space-between" align="center" wrap gap={8}>
              <Title level={5} style={{ margin: 0 }}>
                {t('workspace.billingPage.usageTitle')}
              </Title>
              <Radio.Group
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                size="small"
              >
                <Radio.Button value="current">
                  {t('workspace.billingPage.period.current')}
                </Radio.Button>
                <Radio.Button value="previous">
                  {t('workspace.billingPage.period.previous')}
                </Radio.Button>
                <Radio.Button value="quarter">
                  {t('workspace.billingPage.period.quarter')}
                </Radio.Button>
              </Radio.Group>
            </Flex>

            <Flex vertical gap={16}>
              {metrics.map((metric) => {
                const pct = (metric.used / metric.limit) * 100
                const isWarning = pct > 80

                return (
                  <Flex key={metric.key} vertical gap={4}>
                    <Flex justify="space-between">
                      <Text>{metric.label}</Text>
                      <Text type={isWarning ? 'danger' : 'secondary'}>
                        {metric.key === 'storage'
                          ? `${metric.used} / ${metric.limit} ${metric.unit}`
                          : `${formatNumber(metric.used)} / ${formatNumber(metric.limit)} ${metric.unit}`}
                      </Text>
                    </Flex>
                    <Progress
                      percent={Math.round(pct)}
                      strokeColor={isWarning ? '#faad14' : '#1677ff'}
                      size="small"
                      showInfo={false}
                    />
                  </Flex>
                )
              })}
            </Flex>

            {overageMetrics.length > 0 && (
              <Alert
                type="warning"
                showIcon
                title={t('workspace.billingPage.overageWarning', {
                  period: periodLabel
                })}
                description={
                  <Space orientation="vertical" size={4}>
                    {overageMetrics.map((m) => (
                      <Text key={m.key} type="secondary">
                        {m.label}: {Math.round(m.pct)}%
                      </Text>
                    ))}
                  </Space>
                }
              />
            )}
          </Flex>
        </Card>
      </Flex>
    </WorkspacePageShell>
  )
}
